/**
 * Conversion between the bank-scoped OPC UA node -> lift bindings (UI form
 * state, `Record<describeNode(node), liftIndexString>`) and the generated
 * `OpcUaNodeLiftIndexBindingsT` FlatBuffer Object API — mirrors
 * `lobbyVision2LiftBindingsBuilder.ts`.
 *
 * This binding is authored per bank at the Banks step of the setup wizard,
 * which precedes Lifts & Floors, so real lift UUIDs don't exist yet.
 * `OpcUaNodeLiftIndexEntryT.liftIndex` is the ZERO-BASED POSITION of the LN2
 * lift within the bank's ordered lifts (== CarNum), not a lift UUID — this
 * matches `BankNodeLiftMatrix`'s `bindings` map value directly (`lifts[].id
 * = String(i)`), so both fields are packed as plain string/number — no UUID
 * conversion. LiftCloud resolves `lift_index` -> `bank.Lifts[lift_index].ID`
 * when it assembles the edge CDO (`CDO.OpcUaNodeLiftEntry`, still
 * UUID-based).
 *
 * FlatBuffers-only: never send this map as JSON. Packed to bytes via
 * `packToBytes`, base64-encoded for the request body
 * (`opc_ua_node_lift_mapping_binary`, reusing `opcUaMappingBytesToBase64`),
 * and decoded back with `getRootAsOpcUaNodeLiftIndexBindings(...).unpack()`
 * on load.
 */
import * as flatbuffers from "flatbuffers";

import {
  OpcUaNodeLiftIndexBindings,
  OpcUaNodeLiftIndexBindingsT,
} from "../generated/config/opc-ua-node-lift-index-bindings";
import { OpcUaNodeLiftIndexEntryT } from "../generated/config/opc-ua-node-lift-index-entry";
import { packToBytes } from "../../utils/flatbufferApi";
import { opcUaMappingBytesToBase64 } from "./opcUaCdoBuilder";

/**
 * Build an `OpcUaNodeLiftIndexBindingsT` from the editor's bank-scoped
 * node -> lift-index bindings.
 */
export function editorToOpcUaNodeLiftIndexBindingsT(
  bindings: Record<string, string>,
): OpcUaNodeLiftIndexBindingsT {
  const entries = Object.entries(bindings).map(
    ([node, liftIndex]) => new OpcUaNodeLiftIndexEntryT(node, Number(liftIndex)),
  );
  return new OpcUaNodeLiftIndexBindingsT(entries);
}

/** Pack the editor's bindings map to FlatBuffer binary bytes. FlatBuffers-only — never send this map as JSON. */
export function buildOpcUaNodeLiftIndexBindingsBytes(bindings: Record<string, string>): Uint8Array {
  return packToBytes(editorToOpcUaNodeLiftIndexBindingsT(bindings));
}

/**
 * Base64-encode packed bindings bytes for the bank request body
 * (`opc_ua_node_lift_mapping_binary`). Reuses `opcUaMappingBytesToBase64` —
 * the byte-encoding step is protocol-agnostic.
 */
export function opcUaNodeLiftIndexBindingsToBase64(bytes: Uint8Array): string {
  return opcUaMappingBytesToBase64(bytes);
}

/** Decode packed bindings bytes back into the editor's `Record<node, liftIndexString>` map. */
export function decodeOpcUaNodeLiftIndexBindings(bytes: Uint8Array): Record<string, string> {
  const bindings = OpcUaNodeLiftIndexBindings.getRootAsOpcUaNodeLiftIndexBindings(
    new flatbuffers.ByteBuffer(bytes),
  ).unpack();
  const result: Record<string, string> = {};
  bindings.entries.forEach((entry) => {
    const node = typeof entry.node === "string" ? entry.node : "";
    result[node] = `${entry.liftIndex}`;
  });
  return result;
}
